import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, LayoutChangeEvent, Pressable } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  FadeIn,
} from 'react-native-reanimated';
import { Lock } from 'lucide-react-native';
import * as Haptics from 'expo-haptics';
import { useThemeColors } from '@/lib/useThemeColors';

// ─── Types ──────────────────────────────────────────────────────

export interface ValuationMethodBar {
  method: string;
  label: string;
  min: number;
  max: number;
  pointEstimate: number;
  confidence: number;
  locked?: boolean;
  factors?: { name: string; value: number; description: string }[];
}

export interface SuggestedRange {
  min: number;
  max: number;
  median: number;
}

export interface FootballFieldChartProps {
  methods: ValuationMethodBar[];
  suggestedRange?: SuggestedRange;
  height?: number;
  animated?: boolean;
  onMethodPress?: (method: string) => void;
  currency?: string;
}

// ─── Helpers ────────────────────────────────────────────────────

function formatValuation(amount: number, currency: string = 'CHF'): string {
  if (amount >= 1_000_000_000)
    return `${currency} ${(amount / 1e9).toFixed(amount % 1e9 === 0 ? 0 : 1)}B`;
  if (amount >= 1_000_000)
    return `${currency} ${(amount / 1e6).toFixed(amount % 1e6 === 0 ? 0 : 1)}M`;
  if (amount >= 1_000)
    return `${currency} ${(amount / 1e3).toFixed(amount % 1e3 === 0 ? 0 : 1)}K`;
  return `${currency} ${amount.toFixed(0)}`;
}

function computeSuggestedRange(methods: ValuationMethodBar[]): SuggestedRange {
  const unlocked = methods.filter((m) => !m.locked);
  if (unlocked.length === 0) {
    return { min: 0, max: 0, median: 0 };
  }

  const estimates = unlocked.map((m) => m.pointEstimate).sort((a, b) => a - b);
  const n = estimates.length;

  const q1Index = Math.floor(n * 0.25);
  const q3Index = Math.floor(n * 0.75);
  const q1 = estimates[q1Index] ?? estimates[0]!;
  const q3 = estimates[q3Index] ?? estimates[n - 1]!;
  const medianIndex = Math.floor(n / 2);
  const median =
    n % 2 === 0
      ? ((estimates[medianIndex - 1] ?? 0) + (estimates[medianIndex] ?? 0)) / 2
      : (estimates[medianIndex] ?? 0);

  const iqr = q3 - q1;
  const lowerFence = Math.max(q1 - 1.5 * iqr, Math.min(...estimates));
  const upperFence = Math.min(q3 + 1.5 * iqr, Math.max(...estimates));

  return { min: lowerFence, max: upperFence, median };
}

// ─── Method Color Mapping ───────────────────────────────────────

const METHOD_COLORS: Record<string, string> = {
  berkus: '#00B8B8',
  scorecard: '#FF9500',
  'risk-factor': '#007AFF',
  'vc-method': '#AF52DE',
};

function getMethodColor(method: string, fallback: string): string {
  return METHOD_COLORS[method] ?? fallback;
}

// ─── Constants ──────────────────────────────────────────────────

const ROW_GAP = 10;
const BAR_HEIGHT = 24;
const POINT_MARKER_SIZE = 12;
const STAGGER_DELAY = 120;
const SUGGESTED_EXTRA_DELAY = 300;
const FACTOR_ROW_HEIGHT = 28;

// ─── Bar Row ────────────────────────────────────────────────────
//
// Renders a method label row + a grey track with a colored range
// bar and a point-estimate dot.
//
// Layout uses plain pixel numbers (no percentage strings).
// Animation uses reanimated `entering` prop (FadeIn) which is
// reliable on native — no useAnimatedStyle/useEffect/shared-value
// for visibility.

interface BarRowProps {
  item: ValuationMethodBar;
  chartWidth: number;
  globalMin: number;
  globalMax: number;
  barColor: string;
  animationDelay: number;
  onPress?: () => void;
  expanded: boolean;
  currency: string;
  isSuggested?: boolean;
  pointMarkerColor?: string;
}

function BarRow({
  item,
  chartWidth,
  globalMin,
  globalMax,
  barColor,
  animationDelay,
  onPress,
  expanded,
  currency,
  isSuggested,
  pointMarkerColor,
}: BarRowProps) {
  const colors = useThemeColors();

  // ── Pixel geometry ────────────────────────────────────────────
  const range = globalMax - globalMin || 1;
  const barLeftPx = ((item.min - globalMin) / range) * chartWidth;
  const barWidthPx = Math.max(((item.max - item.min) / range) * chartWidth, 2);
  const pointLeftPx = ((item.pointEstimate - globalMin) / range) * chartWidth - POINT_MARKER_SIZE / 2;

  const barOpacity = item.locked ? 0.35 : 0.25 + (item.confidence / 100) * 0.65;
  const lockedColor = colors.light[500];
  const activeColor = item.locked ? lockedColor : barColor;
  const dotColor = pointMarkerColor ?? activeColor;

  // ── Expand/collapse for factors ───────────────────────────────
  const expandHeight = useSharedValue(0);
  const factorCount = item.factors?.length ?? 0;
  const targetExpandHeight = expanded ? factorCount * FACTOR_ROW_HEIGHT + 8 : 0;

  useEffect(() => {
    expandHeight.value = withSpring(targetExpandHeight, { damping: 18, stiffness: 160 });
  }, [targetExpandHeight]);

  const expandStyle = useAnimatedStyle(() => ({
    height: expandHeight.value,
    overflow: 'hidden' as const,
  }));

  const handlePress = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onPress?.();
  }, [onPress]);

  const a11yLabel = item.locked
    ? `${item.label}, locked`
    : `${item.label}, range ${formatValuation(item.min, currency)} to ${formatValuation(item.max, currency)}, estimate ${formatValuation(item.pointEstimate, currency)}, confidence ${item.confidence}%`;

  return (
    <Animated.View entering={FadeIn.delay(animationDelay).duration(400)}>
      <Pressable
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        style={({ pressed }) => ({ opacity: pressed ? 0.85 : 1, paddingVertical: 4 })}
      >
        {/* Label row */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 }}>
            {!isSuggested && (
              <View
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  backgroundColor: activeColor,
                  opacity: item.locked ? 0.4 : 1,
                }}
              />
            )}
            <Text
              className={isSuggested ? 'text-xs font-bold' : 'text-xs font-semibold'}
              style={{
                color: isSuggested ? colors.success : colors.text.primary,
              }}
              numberOfLines={1}
            >
              {item.label}
            </Text>
            {item.locked && <Lock size={12} color={colors.text.tertiary} />}
          </View>
          <Text
            className="text-xs font-medium"
            style={{
              color: item.locked ? colors.text.tertiary : colors.text.secondary,
            }}
            numberOfLines={1}
          >
            {item.locked
              ? 'Locked'
              : `${formatValuation(item.min, currency)} – ${formatValuation(item.max, currency)}`}
          </Text>
        </View>

        {/* Bar track */}
        <View
          style={{
            width: chartWidth,
            height: BAR_HEIGHT,
            backgroundColor: colors.surface.secondary,
            borderRadius: 6,
          }}
        >
          {/* Range bar */}
          <View
            style={{
              position: 'absolute',
              top: 2,
              bottom: 2,
              left: barLeftPx,
              width: barWidthPx,
              backgroundColor: activeColor,
              borderRadius: 4,
              opacity: barOpacity,
            }}
          />

          {/* Point-estimate dot */}
          {!item.locked && (
            <View
              style={{
                position: 'absolute',
                top: (BAR_HEIGHT - POINT_MARKER_SIZE) / 2,
                left: pointLeftPx,
                width: POINT_MARKER_SIZE,
                height: POINT_MARKER_SIZE,
                borderRadius: POINT_MARKER_SIZE / 2,
                backgroundColor: dotColor,
                borderWidth: 2,
                borderColor: colors.surface.primary,
                shadowColor: '#000',
                shadowOpacity: 0.2,
                shadowRadius: 3,
                shadowOffset: { width: 0, height: 1 },
                elevation: 3,
              }}
            />
          )}

          {/* Lock overlay */}
          {item.locked && (
            <View
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Lock size={14} color={colors.text.tertiary} />
            </View>
          )}
        </View>
      </Pressable>

      {/* Expanded factors */}
      {factorCount > 0 && (
        <Animated.View style={expandStyle}>
          <View style={{ paddingLeft: 16, paddingTop: 4 }}>
            {item.factors!.map((factor) => (
              <View
                key={factor.name}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  height: FACTOR_ROW_HEIGHT,
                  paddingRight: 4,
                }}
              >
                <Text
                  className="text-xs"
                  style={{ color: colors.text.secondary, flex: 1 }}
                  numberOfLines={1}
                >
                  {factor.name}
                </Text>
                <Text
                  className="text-xs font-semibold"
                  style={{ color: colors.text.primary, marginLeft: 8 }}
                >
                  {formatValuation(factor.value, currency)}
                </Text>
              </View>
            ))}
          </View>
        </Animated.View>
      )}
    </Animated.View>
  );
}

// ─── Scale Ticks ────────────────────────────────────────────────

interface ScaleTicksProps {
  globalMin: number;
  globalMax: number;
  chartWidth: number;
  currency: string;
}

function ScaleTicks({ globalMin, globalMax, chartWidth, currency }: ScaleTicksProps) {
  const colors = useThemeColors();
  const range = globalMax - globalMin || 1;
  const tickCount = 5;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const value = globalMin + (range / (tickCount - 1)) * i;
    return { value };
  });

  return (
    <View style={{ width: chartWidth, flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
      {ticks.map((tick, i) => (
        <Text
          key={i}
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            textAlign: i === 0 ? 'left' : i === tickCount - 1 ? 'right' : 'center',
            flex: i === 0 || i === tickCount - 1 ? 0 : 1,
          }}
          numberOfLines={1}
        >
          {formatValuation(tick.value, currency)}
        </Text>
      ))}
    </View>
  );
}

// ─── Main Component ─────────────────────────────────────────────

export function FootballFieldChart({
  methods,
  suggestedRange: suggestedRangeProp,
  height,
  animated = true,
  onMethodPress,
  currency = 'CHF',
}: FootballFieldChartProps) {
  const colors = useThemeColors();
  const [chartWidth, setChartWidth] = useState(0);
  const [expandedMethod, setExpandedMethod] = useState<string | null>(null);

  const suggestedRange = useMemo(
    () => suggestedRangeProp ?? computeSuggestedRange(methods),
    [suggestedRangeProp, methods],
  );

  const { globalMin, globalMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const m of methods) {
      if (m.min < min) min = m.min;
      if (m.max > max) max = m.max;
    }
    if (suggestedRange.min > 0 || suggestedRange.max > 0) {
      if (suggestedRange.min < min) min = suggestedRange.min;
      if (suggestedRange.max > max) max = suggestedRange.max;
    }
    const padding = (max - min) * 0.05;
    return { globalMin: Math.max(0, min - padding), globalMax: max + padding };
  }, [methods, suggestedRange]);

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setChartWidth(e.nativeEvent.layout.width);
  }, []);

  const handleMethodPress = useCallback(
    (method: string, hasFactors: boolean) => {
      if (hasFactors) setExpandedMethod((prev) => (prev === method ? null : method));
      onMethodPress?.(method);
    },
    [onMethodPress],
  );

  const suggestedBar: ValuationMethodBar | null = useMemo(() => {
    if (suggestedRange.min === 0 && suggestedRange.max === 0) return null;
    return {
      method: 'suggested',
      label: 'Suggested Range',
      min: suggestedRange.min,
      max: suggestedRange.max,
      pointEstimate: suggestedRange.median,
      confidence: 100,
    };
  }, [suggestedRange]);

  const ROW_ESTIMATED_HEIGHT = 52; // label + bar + padding
  const totalRows = methods.length + (suggestedBar ? 1 : 0);
  const separatorHeight = suggestedBar ? 20 : 0;
  const computedHeight =
    height ?? totalRows * (ROW_ESTIMATED_HEIGHT + ROW_GAP) + separatorHeight + 40;

  const suggestedDelay = methods.length * STAGGER_DELAY + SUGGESTED_EXTRA_DELAY;
  const medianDotColor = colors.error;

  return (
    <View style={{ minHeight: computedHeight }}>
      {/* onLayout wrapper measures available width */}
      <View onLayout={onLayout}>
        {/* Only render bars once we know the pixel width */}
        {chartWidth > 0 && (
          <View>
            {/* Method bars */}
            {methods.map((method, index) => (
              <View key={method.method} style={{ marginBottom: ROW_GAP }}>
                <BarRow
                  item={method}
                  chartWidth={chartWidth}
                  globalMin={globalMin}
                  globalMax={globalMax}
                  barColor={getMethodColor(method.method, colors.primary[500])}
                  animationDelay={index * STAGGER_DELAY}
                  onPress={() => handleMethodPress(method.method, (method.factors?.length ?? 0) > 0)}
                  expanded={expandedMethod === method.method}
                  currency={currency}
                />
              </View>
            ))}

            {/* Dashed separator */}
            {suggestedBar && (
              <View style={{ height: separatorHeight, justifyContent: 'center', marginBottom: ROW_GAP }}>
                <Svg width={chartWidth} height={2}>
                  <Line
                    x1={0} y1={1} x2={chartWidth} y2={1}
                    stroke={colors.border.medium}
                    strokeWidth={1.5}
                    strokeDasharray="8,4"
                  />
                </Svg>
              </View>
            )}

            {/* Suggested range bar — red dot marks the median */}
            {suggestedBar && (
              <View style={{ marginBottom: ROW_GAP }}>
                <BarRow
                  item={suggestedBar}
                  chartWidth={chartWidth}
                  globalMin={globalMin}
                  globalMax={globalMax}
                  barColor={colors.success}
                  animationDelay={suggestedDelay}
                  onPress={() => onMethodPress?.('suggested')}
                  expanded={false}
                  currency={currency}
                  isSuggested
                  pointMarkerColor={medianDotColor}
                />
              </View>
            )}

            {/* Scale ticks */}
            <ScaleTicks
              globalMin={globalMin}
              globalMax={globalMax}
              chartWidth={chartWidth}
              currency={currency}
            />
          </View>
        )}
      </View>
    </View>
  );
}
